import React from 'react';
import { Skeleton, Tooltip, LinearProgress } from '@mui/material';
import { Pets } from '@mui/icons-material';

const formatCurrency = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n}`;
};

const SPECIES_COLORS = {
  VANNAMEI: { bar: '#10b981', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  MONODON: { bar: '#3b82f6', bg: 'bg-blue-50', text: 'text-blue-700' },
  ROHU: { bar: '#f59e0b', bg: 'bg-amber-50', text: 'text-amber-700' },
  CATLA: { bar: '#8b5cf6', bg: 'bg-purple-50', text: 'text-purple-700' },
  MRIGAL: { bar: '#ec4899', bg: 'bg-pink-50', text: 'text-pink-700' },
  PANGASIUS: { bar: '#ef4444', bg: 'bg-red-50', text: 'text-red-700' },
  TILAPIA: { bar: '#06b6d4', bg: 'bg-cyan-50', text: 'text-cyan-700' },
};
const DEFAULT_COLOR = { bar: '#6b7280', bg: 'bg-gray-50', text: 'text-gray-700' };

const SpeciesRiskSection = ({ data = [], loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <Skeleton variant="text" width={200} height={28} />
        <Skeleton variant="rounded" height={220} sx={{ mt: 2 }} />
      </div>
    );
  }

  const totalExposure = data.reduce((s, d) => s + d.exposure, 0);

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-center gap-2 mb-4">
        <Pets sx={{ fontSize: 18, color: '#6366f1' }} />
        <h3 className="text-sm font-semibold text-gray-700">Species Risk Distribution</h3>
        <span className="ml-auto text-xs text-gray-400">{data.length} species</span>
      </div>

      {data.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-sm text-gray-400">
          No species data available with current filters
        </div>
      ) : (
        <div className="space-y-3 max-h-[350px] overflow-y-auto pr-1">
          {data.map((sp) => {
            const col = SPECIES_COLORS[sp.species.toUpperCase()] || DEFAULT_COLOR;
            const pct = totalExposure > 0 ? (sp.exposure / totalExposure) * 100 : 0;
            const claimRatio = sp.policies > 0 ? ((sp.claims / sp.policies) * 100).toFixed(1) : 0;

            return (
              <div key={sp.species} className={`rounded-xl p-3 ${col.bg} border border-gray-100`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-semibold ${col.text}`}>
                    {sp.species.charAt(0) + sp.species.slice(1).toLowerCase()}
                  </span>
                  <span className="text-xs text-gray-500 font-medium">{pct.toFixed(1)}% exposure</span>
                </div>

                {/* Exposure bar */}
                <Tooltip title={`Exposure: ${formatCurrency(sp.exposure)}`} arrow>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: '#e5e7eb',
                      '& .MuiLinearProgress-bar': { backgroundColor: col.bar, borderRadius: 3 },
                      mb: 1.5,
                    }}
                  />
                </Tooltip>

                <div className="flex items-center gap-4 text-xs text-gray-600">
                  <span>
                    <strong>{sp.policies}</strong> policies
                  </span>
                  <span>
                    <strong>{sp.claims}</strong> claims
                  </span>
                  <span>
                    Claim Ratio:{' '}
                    <strong
                      className={
                        claimRatio > 30
                          ? 'text-red-600'
                          : claimRatio > 15
                          ? 'text-amber-600'
                          : 'text-emerald-600'
                      }
                    >
                      {claimRatio}%
                    </strong>
                  </span>
                  <span className="ml-auto font-semibold text-gray-700">
                    {formatCurrency(sp.exposure)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      {data.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
          <span>
            Total Exposure: <strong className="text-gray-700">{formatCurrency(totalExposure)}</strong>
          </span>
          <span>
            Total Policies: <strong className="text-gray-700">{data.reduce((s, d) => s + d.policies, 0)}</strong>
          </span>
        </div>
      )}
    </div>
  );
};

export default SpeciesRiskSection;
